import { BootstrapColor } from '@/types/css-variables.types';
import { PlatformFeedbackDetailDto } from '@data/_generated-api/platform-api/models/platform-feedback-detail-dto';
import { PlatformFeedbackStatus } from '@data/_generated-api/platform-api/models/platform-feedback-status';
import { PlatformFeedbackType } from '@data/_generated-api/platform-api/models/platform-feedback-type';
import { SupportFeedbackKind } from './support-attachment.models';

export type SupportStatusKey = 'new' | 'inProgress' | 'awaitingFeedback' | 'resolved' | 'declined';

/** Volgorde zoals de statuskiezer en de filters ze tonen. */
export const SUPPORT_STATUSES: readonly PlatformFeedbackStatus[] = [
  PlatformFeedbackStatus.New,
  PlatformFeedbackStatus.InProgress,
  PlatformFeedbackStatus.AwaitingFeedback,
  PlatformFeedbackStatus.Resolved,
  PlatformFeedbackStatus.Declined,
];

/** Open statussen: het standaardfilter in het beheer. Een reactie op zo'n melding zet ze op "wacht op reactie". */
export const SUPPORT_OPEN_STATUSES: readonly PlatformFeedbackStatus[] = [
  PlatformFeedbackStatus.New,
  PlatformFeedbackStatus.InProgress,
  PlatformFeedbackStatus.AwaitingFeedback,
];

export const SUPPORT_TYPES: readonly PlatformFeedbackType[] = [
  PlatformFeedbackType.Bug,
  PlatformFeedbackType.Question,
  PlatformFeedbackType.Idea,
];

const STATUS_KEY: Record<PlatformFeedbackStatus, SupportStatusKey> = {
  [PlatformFeedbackStatus.New]: 'new',
  [PlatformFeedbackStatus.InProgress]: 'inProgress',
  [PlatformFeedbackStatus.AwaitingFeedback]: 'awaitingFeedback',
  [PlatformFeedbackStatus.Resolved]: 'resolved',
  [PlatformFeedbackStatus.Declined]: 'declined',
};

const STATUS_COLOR: Record<PlatformFeedbackStatus, BootstrapColor> = {
  [PlatformFeedbackStatus.New]: 'highlight',
  [PlatformFeedbackStatus.InProgress]: 'primary',
  [PlatformFeedbackStatus.AwaitingFeedback]: 'warning',
  [PlatformFeedbackStatus.Resolved]: 'success',
  [PlatformFeedbackStatus.Declined]: 'elegant',
};

const TYPE_KEY: Record<PlatformFeedbackType, SupportFeedbackKind> = {
  [PlatformFeedbackType.Bug]: 'bug',
  [PlatformFeedbackType.Question]: 'question',
  [PlatformFeedbackType.Idea]: 'idea',
};

const TYPE_ICON: Record<SupportFeedbackKind, string> = {
  bug: 'bug',
  question: 'circle-question',
  idea: 'lightbulb',
};

/** Grenzen van de prioriteit (volgorde van uitvoering), gelijk aan de backend (ModelConstraints). */
export const SUPPORT_PRIORITY_MIN = 1;
export const SUPPORT_PRIORITY_MAX = 999;

/**
 * Invoer van het prioriteitsveld: leeg = geen prioriteit (null), een geheel getal
 * binnen de grenzen = die prioriteit, al de rest = ongeldig (undefined).
 */
export function parsePriorityInput(raw: string): number | null | undefined {
  const text = raw.trim();
  if (text === '') return null;
  if (!/^\d+$/.test(text)) return undefined;
  const value = Number(text);
  return value >= SUPPORT_PRIORITY_MIN && value <= SUPPORT_PRIORITY_MAX ? value : undefined;
}

/** Korte weergave van een prioriteit ("P1"); leeg zonder prioriteit. */
export function priorityLabel(priority: number | null | undefined): string {
  return priority != null ? `P${priority}` : '';
}

/** Vertaalsleutel onder `support.status`; onbekend of ontbrekend telt als nieuw. */
export function statusKey(status: PlatformFeedbackStatus | undefined): SupportStatusKey {
  return (status !== undefined && STATUS_KEY[status]) || 'new';
}

export function statusColor(status: PlatformFeedbackStatus | undefined): BootstrapColor {
  return (status !== undefined && STATUS_COLOR[status]) || 'highlight';
}

/** Vertaalsleutel onder `support.types`; onbekend of ontbrekend telt als vraag. */
export function typeKey(type: PlatformFeedbackType | undefined): SupportFeedbackKind {
  return (type !== undefined && TYPE_KEY[type]) || 'question';
}

export function typeIcon(type: PlatformFeedbackType | undefined): string {
  return TYPE_ICON[typeKey(type)];
}

/** Alleen deze bijlagetypes worden als afbeelding getoond; andere houden hun bestandsnaam als plaatshouder. */
export const ALLOWED_ATTACHMENT_TYPES: ReadonlySet<string> = new Set(['image/jpeg', 'image/png']);

export interface SupportFieldRow {
  /** Vertaalsleutel onder `support.detail`. */
  key: 'steps' | 'expected' | 'actual' | 'description';
  value: string;
}

export interface SupportContextRow {
  key: string;
  value: string;
}

/** Geparste context als tabel, of de ruwe tekst als de JSON niet leesbaar is. */
export interface SupportContextView {
  rows?: SupportContextRow[];
  raw?: string;
}

/** Ingevulde velden in leesvolgorde: bug toont stappen, verwachting en resultaat; de rest alleen de beschrijving. */
export function detailFields(dto: PlatformFeedbackDetailDto | undefined): SupportFieldRow[] {
  if (!dto) return [];
  const candidates: { key: SupportFieldRow['key']; value: string | null | undefined }[] =
    dto.type === PlatformFeedbackType.Bug
      ? [
          { key: 'steps', value: dto.stepsToReproduce },
          { key: 'expected', value: dto.expectedBehavior },
          { key: 'actual', value: dto.description },
        ]
      : [{ key: 'description', value: dto.description }];
  return candidates.filter(row => !!row.value?.trim()).map(row => ({ key: row.key, value: row.value!.trim() }));
}

export function parseContext(json: string | null | undefined): SupportContextView | undefined {
  if (!json) return undefined;
  try {
    const parsed: unknown = JSON.parse(json);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return { raw: json };
    const rows = Object.entries(parsed as Record<string, unknown>).map(([key, value]) => ({ key, value: contextValue(value) }));
    return { rows };
  } catch {
    return { raw: json };
  }
}

function contextValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}
